/**
 * Hands the day's movements and the stand plan to the boards.
 *
 * Renders nothing, sits in the shell, and runs under both adapters. It reads
 * the same flight record and allocation plan the flight screens do, through
 * the same adapter, so the boards and the screens cannot disagree.
 *
 * It sits on every page, so it costs nothing until it is needed: the reads
 * start only once the legacy engine — the only reader of what it fills — has
 * loaded, and they share the screens' cache without a timer of their own, so
 * no page asks the platform to work out the allocation day an extra time.
 * Queued through `whenDemoLoaded`, so a session that never opens a board
 * never downloads one.
 */
import { useEffect, useMemo, useState } from "react";
import { useAllocDay, useAodbDay, useWorkOrders } from "../../api/queries";
import { demoIfLoaded, whenDemoLoaded } from "../../demo/domain/load";
import { flightsFromRecord, onPlannedTimes, registerFromPlan, serviceDateToday } from "../../domain/flightWorld";
import { AERODROME_OFFSET_HOURS } from "../../lib/format";
import { blocksFromWorkOrders } from "./standBlocks";

export function FlightsFeed() {
  const [engine, setEngine] = useState(() => demoIfLoaded() !== null);
  useEffect(() => {
    if (!engine) whenDemoLoaded(() => setEngine(true));
  }, [engine]);

  const record = useAodbDay(serviceDateToday(new Date()), { enabled: engine, poll: false });
  const alloc = useAllocDay({ enabled: engine, poll: false });
  const orders = useWorkOrders();

  const movements = record.data?.items;
  const day = alloc.data;
  const woItems = orders.data?.items;
  const flights = useMemo(() => (movements ? flightsFromRecord(movements, day) : null), [movements, day]);
  /* the same footing as the screens: the stays as planned */
  const register = useMemo(
    () => (day && flights ? onPlannedTimes(registerFromPlan(day, new Date()), flights) : null),
    [day, flights],
  );

  useEffect(() => {
    if (!flights || !register) return;
    const blocks = blocksFromWorkOrders(register.stands, woItems ?? []);
    whenDemoLoaded(() => {
      void import("../../domain/flightsFeed").then((m) =>
        m.feedFlights(flights, register, blocks, AERODROME_OFFSET_HOURS, new Date()),
      );
    });
  }, [flights, register, woItems]);

  return null;
}
